import { Shuffle, ArrowLeftRight } from 'lucide-react';
import { Button } from '../ui/button';
import { firstName } from '../../lib/play';

// Snake draft by rating so both squads end up near-equal in strength.
export function suggestSquads(ids, ratingOf) {
  const ranked = [...ids].sort((x, y) => (ratingOf[y]?.rating || 0) - (ratingOf[x]?.rating || 0));
  const a = [];
  const b = [];
  ranked.forEach((id, i) => ((i % 4 === 0 || i % 4 === 3) ? a : b).push(id));
  while (a.length - b.length > 1) b.push(a.pop());
  while (b.length - a.length > 1) a.push(b.pop());
  return [a, b];
}

const TONE = [
  { name: 'Red', chip: 'bg-rose-500/15 border-rose-500/50 text-rose-900 dark:text-rose-200', dot: 'bg-rose-500' },
  { name: 'Blue', chip: 'bg-sky-500/15 border-sky-500/50 text-sky-900 dark:text-sky-200', dot: 'bg-sky-500' },
];

export default function SquadEditor({ squads, ratingOf, onChange, onReSuggest, canEdit }) {
  const [a, b] = squads;
  const total = (ids) => ids.reduce((sum, id) => sum + (ratingOf[id]?.rating || 0), 0);
  const move = (id, from) => {
    if (!canEdit) return;
    const next = from === 0 ? [a.filter((x) => x !== id), [...b, id]] : [[...a, id], b.filter((x) => x !== id)];
    onChange(next);
  };

  return (
    <div className="rounded-2xl border p-3 space-y-3" data-testid="squad-editor">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold">Squads</p>
          <p className="text-xs text-muted-foreground">Tap a name to move them across. Pairs rotate inside each squad.</p>
        </div>
        {canEdit && (
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={onReSuggest} data-testid="squad-auto-balance">
            <Shuffle className="w-3.5 h-3.5 mr-1" /> Auto-balance
          </Button>
        )}
      </div>
      {[a, b].map((ids, side) => (
        <div key={TONE[side].name} data-testid={`squad-${side === 0 ? 'a' : 'b'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${TONE[side].dot}`} /> {TONE[side].name}
            </p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {ids.length} players · {ids.length ? Math.round(total(ids) / ids.length) : 0} avg
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ids.map((id) => (
              <button key={id} type="button" onClick={() => move(id, side)} data-testid={`squad-chip-${id}`}
                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-full border text-xs font-semibold transition-colors ${TONE[side].chip}`}>
                {firstName(ratingOf[id]?.name || 'Guest')}
                <span className="text-xs tabular-nums opacity-70">{ratingOf[id]?.rating}</span>
                {canEdit && <ArrowLeftRight className="w-3 h-3 opacity-60" />}
              </button>
            ))}
            {ids.length === 0 && <p className="text-xs text-muted-foreground">Empty — move players here.</p>}
          </div>
        </div>
      ))}
      {(a.length < 2 || b.length < 2) && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="squad-too-small">
          Each squad needs at least 2 players.
        </p>
      )}
    </div>
  );
}
